import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { get, put } from '@/services/http'
import { endpoints } from '@/services/endpoints'
import { queryKeys } from '@/services/queryClient'

/**
 * The organisation record — Settings → General.
 *
 * LIVE, and narrow: `GET /account` and `PUT /account` carry three editable
 * fields between them, `alias`, `org_name` and `logo_file_id`. That is the whole
 * account as far as this API is concerned.
 *
 * Separate from `features/org/settings.js`, which reads the fixture-backed
 * `/org/settings` and still answers the parts of that page with no endpoint
 * behind them. The two coexist deliberately: this one is real, that one is the
 * demo universe, and mixing them into a single hook would make it impossible to
 * tell at a call site which half was which.
 *
 * ── Both routes require `account.manage` ────────────────────────────────
 * So a signed-in user without it gets a 403 on load, not an empty form. The
 * page checks the permission map from `/auth/me` before rendering and says so,
 * because a form that loads and then cannot save is worse than one that never
 * offered.
 */

/** The wire shape is snake_case, the app is camelCase. Converted at this seam. */
function toAccount(payload) {
  if (!payload) return null
  return {
    id: payload.id ?? null,
    /* What people type to sign in. Changing it emails every active user in the
       organisation and invalidates the alias they have memorised — see the
       warning the form carries. */
    alias: payload.alias ?? '',
    name: payload.org_name ?? '',
    logoFileId: payload.logo_file_id ?? null,
    /* A presigned address with a five-minute life. Never persisted anywhere;
       re-read with the record. */
    logoUrl: payload.logo_view_url ?? null,
  }
}

export function useAccount({ enabled = true } = {}) {
  return useQuery({
    queryKey: queryKeys.account,
    queryFn: async ({ signal }) => toAccount(await get(endpoints.account.current, { signal })),
    enabled,
    /* `logo_view_url` expires in five minutes, so a record held longer than that
       renders a broken image. Re-read rather than cached across visits. */
    gcTime: 0,
    retry: false,
  })
}

/**
 * Save it.
 *
 * ── Every field, every time ─────────────────────────────────────────────
 * `AccountUpdate` gives none of its three fields a default, so all three must be
 * present on every request — including `logo_file_id`, which is `null` for "no
 * logo" and the existing id for "leave it alone". Omitting one is a 422, not a
 * field left untouched. The form therefore submits the whole record rather than
 * only what changed.
 *
 * @param {object} draft
 * @param {string} draft.alias
 * @param {string} draft.name
 * @param {string|null} draft.logoFileId
 */
export function useSaveAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (draft) =>
      toAccount(
        await put(endpoints.account.current, {
          alias: draft.alias.trim(),
          org_name: draft.name.trim(),
          logo_file_id: draft.logoFileId ?? null,
        }),
      ),
    // A rejected alias or a taken name is an answer, not a fault to retry.
    retry: false,
    onSuccess: (account) => {
      client.setQueryData(queryKeys.account, account)
      /* The organisation's name and logo ride on `/auth/me`, which the shell
         renders from — so a rename that left the top bar reading the old name
         would look like the save had failed. */
      client.invalidateQueries({ queryKey: queryKeys.session })
    },
  })
}
